import React from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert, assertAndContinue} from 'Asserts';
import {ActionOrigin, ANALYTICS_MODULE, ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import * as context from 'Context';
import {getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';
import {waitForEntryOrTemplateSaved} from 'notebook/entries/BaseEntryUtil';
import {
  saveExistingPlateWithPlateRecord,
  saveNewPlateWithPlateRecord,
} from 'notebook/integrations/plate-maps/FixedPlateEditorUtil';
import {NoteLinkedPlateDesignNotePart} from 'notebook/integrations/plate-maps/plate-design/NoteLinkedPlateDesignTypes';
import {
  getPlateDesignName,
  makeNoteLinkedPlateDesign,
  makeNoteLinkedPlateDesignData,
} from 'notebook/integrations/plate-maps/plate-design/NoteLinkedPlateDesignUtil';
import ParametersForPlateMapWrapper from 'notebook/integrations/procedures/ParametersForPlateMapWrapper';
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import NoteConstants from 'notebook/notes/NoteConstants';
import {ACTION_SYNC_FILE} from 'notebook/store/VersioningConstants';
import {NotePartIndex} from 'notebook/util/EntryNoteIndexUtil';
import {generateUniqueName} from 'notebook/util/GenerateUniqueName';
import {Note, NotePart} from 'notebook/util/NoteUtilConstants';
import {Worksheet} from 'notebook/worksheets/WorksheetConstants';
import {WorksheetContext} from 'notebook/worksheets/WorksheetContext';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  CREATE_PLATE_DESIGN_STARTED_EVENT,
  CREATE_PLATE_DESIGN_FINISHED_EVENT,
  getGridSizeBucket,
} from 'performance/processInventoryEvents';
import AnnotateFillPlateModal, {
  AnnotateFillPlateModalProps,
} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModal';
import {InitiatingPlateDesignStateWithNotePart} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {PlateDesignVersionRef} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import ModalAnchor from 'ui-library/ModalAnchor';
import APIUtil from 'util/APIUtil';

export interface NotebookAnnotateFillPlateModalProps {
  onRequestSubmit: () => void;
  onRequestClose: () => void;
  fileId: string;
  plateRecordNames: Array<string>;
  plateDesignNames: Array<string>;
  note: Note;
  notePartIndex: NotePartIndex | null;
  eventGroupId: string;
  upsertNotePart: (notePart: NotePart) => void;
  /** If this modal was initiated from a plate design, information about that design. */
  initiatingPlateDesignState?: InitiatingPlateDesignStateWithNotePart;
  locked: boolean;
}

/**
 * @package exported for testing
 */
export function getStudyablePropsFromWorksheet(
  worksheet: Worksheet | null
): AnnotateFillPlateModalProps['studyableProps'] {
  return worksheet && worksheet.studies.length > 0
    ? {
        initialStudyIds: worksheet.studies.map((study) => getAccessibleOrInaccessibleId(study)),
        studySearchInputProps: {
          // Ensure that a created plate is associated with the same studies as a worksheet.
          isReadOnly: true,
          // Disallow opening studies since we are in a modal.
          hasNonInteractiveChips: true,
        },
      }
    : {
        initialStudyIds: [],
        studySearchInputProps: {},
      };
}

/**
 * Bindings for the AnnotateFillPlateModal component to be used in the NoteEditor.
 *
 * Automatically extracts relevant studies / procedures information from {@link WorksheetContext} if available
 * and passes it to the AnnotateFillPlateModal.
 *
 * @param props.onRequestSubmit - hook when modal is submitted - should close modal.
 * @param props.onRequestClose - hook when modal is cancelled - should close modal.
 * @param props.fileId - api identifier id of the notebook document.
 * @param props.plateRecordNames - existing plate record names.
 * @param props.plateDesignNames - existing plate design names.
 * @param props.notePartIndex - location to insert the plate map in the document.
 * @param props.locked - whether the document is locked from editing.
 */
export default function NotebookAnnotateFillPlateModal(
  props: NotebookAnnotateFillPlateModalProps
): JSX.Element {
  const {worksheet} = WorksheetContext.useContext();

  const existingPlateDesignNotePart = props.initiatingPlateDesignState?.plateDesignNotePart;

  let existingPlateDesignNames = props.plateDesignNames;
  // We want to allow the name to stay the same, so don't dedupe against itself.
  if (existingPlateDesignNotePart != null) {
    const currentName = getPlateDesignName(existingPlateDesignNotePart.item.data.plateDesign);
    existingPlateDesignNames = existingPlateDesignNames.filter((name) => name !== currentName);
  }

  return (
    <ParametersForPlateMapWrapper>
      {({
        isInProcedure,
        plateDataParameters,
        plateMaterialInputs,
        procedureStudyDesignType,
        procedureId,
        procedureTestOrderConfig,
        procedureMethodExecutionInstanceId,
      }) => (
        <ModalAnchor open={true}>
          <AnnotateFillPlateModal
            onRequestSubmit={props.onRequestSubmit}
            onRequestClose={props.onRequestClose}
            saveExistingPlate={async ({
              plateMap,
              grid,
              schemaFieldValues,
              appendStudyIds,
              annotatedDataParameterIds,
              annotatedSchemaFieldIds,
              callbackOnError,
            }) => {
              await context.usingAsync(
                {immediateAutoSaverQueueSaveAction: ACTION_SYNC_FILE},
                null,
                async () => {
                  const name = generateUniqueName(props.plateRecordNames, `Updated ${grid.name}`, {
                    allowNoSuffix: true,
                    addSpaceAfterPrefix: true,
                  });
                  assert(
                    props.notePartIndex != null,
                    `notePartIndex should not be null when saving existing plate for ${APIUtil.apiIdToType(props.fileId)}`,
                    {fileId: props.fileId}
                  );
                  await saveExistingPlateWithPlateRecord({
                    plateMap,
                    grid,
                    plateSchemaFieldValues: schemaFieldValues,
                    fileId: props.fileId,
                    plateRecordName: name,
                    notePartIndex: props.notePartIndex,
                    annotatedDataParameterIds,
                    annotatedSchemaFieldIds,
                    eventGroupId: props.eventGroupId,
                    appendStudyIds,
                    plateDesignVersionRef: props.initiatingPlateDesignState?.plateDesignVersionRef || null,
                    procedureMethodExecutionInstanceId,
                    callbackOnError,
                  });
                }
              );
            }}
            saveNewPlate={async ({
              plateMap,
              createPlatePaneState,
              annotatedDataParameterIds,
              annotatedSchemaFieldIds,
              callbackOnError,
            }) => {
              await context.usingAsync(
                {immediateAutoSaverQueueSaveAction: ACTION_SYNC_FILE},
                null,
                async () => {
                  const name = generateUniqueName(
                    props.plateRecordNames,
                    `Created ${createPlatePaneState.name || 'plate'}`,
                    {allowNoSuffix: true, addSpaceAfterPrefix: true}
                  );
                  assert(
                    props.notePartIndex != null,
                    `notePartIndex should not be null when saving new plate for ${APIUtil.apiIdToType(props.fileId)}`,
                    {fileId: props.fileId}
                  );
                  await saveNewPlateWithPlateRecord({
                    plateMap,
                    createPlatePaneState,
                    fileId: props.fileId,
                    plateRecordName: name,
                    notePartIndex: props.notePartIndex,
                    annotatedDataParameterIds,
                    annotatedSchemaFieldIds,
                    eventGroupId: props.eventGroupId,
                    plateDesignVersionRef: props.initiatingPlateDesignState?.plateDesignVersionRef || null,
                    procedureMethodExecutionInstanceId,
                    callbackOnError,
                  });
                }
              );
            }}
            savePlateDesign={({
              plateDesignState,
              plateMap,
              annotatedDataParameterIds,
              annotatedSchemaFieldIds,
            }) => {
              context.using({immediateAutoSaverQueueSaveAction: ACTION_SYNC_FILE}, null, () => {
                // The name in state is already protected against duplicates.
                const name =
                  plateDesignState.name ||
                  generateUniqueName(existingPlateDesignNames, 'Plate Design', {
                    allowNoSuffix: true,
                    addSpaceAfterPrefix: true,
                  });
                const data = makeNoteLinkedPlateDesignData({
                  plateDesignState: {...plateDesignState, name},
                  plateMap,
                  // Preserving the plateDesignCid will result in creating a new version of the plate design,
                  // instead of creating a whole new plate design.
                  plateDesignCid: existingPlateDesignNotePart?.item.data.cid ?? null,
                  annotatedDataParameterIds,
                  annotatedSchemaFieldIds,
                  // Passed through to keep the optimistic render correct, but not sent in the patch.
                  hasPlateRecords: existingPlateDesignNotePart?.item.data.hasPlateRecords ?? false,
                });
                let notePartToUpsert: NoteLinkedPlateDesignNotePart;
                if (existingPlateDesignNotePart) {
                  notePartToUpsert = {
                    ...existingPlateDesignNotePart,
                    item: {
                      ...existingPlateDesignNotePart.item,
                      data,
                      cid: _.uniqueId('note-linked-plate-design-'),
                    },
                  };
                } else {
                  const item = makeNoteLinkedPlateDesign(data);
                  notePartToUpsert = {...makeDefaultItem(NoteConstants.NOTE_LINKED_OBJECT), item};
                }
                // If the document is locked (due to concurrent editing), we won't be able to insert the plate
                // design. Fail here instead.
                if (props.locked) {
                  notifications.error(
                    'Unable to save plate design due to changes made to your entry during configuration.'
                  );
                  return;
                }
                const newPlateDesign = existingPlateDesignNotePart == null;
                const profilerId = _.uniqueId();
                if (newPlateDesign) {
                  logProfilerTimingEvent({...CREATE_PLATE_DESIGN_STARTED_EVENT, id: profilerId});
                }
                props.upsertNotePart(notePartToUpsert);
                if (newPlateDesign) {
                  const profilerMetadata = {
                    distributionMetadata: {plate_size_bucket: getGridSizeBucket(plateMap.wells.length)},
                    actionMetadata: {
                      number_wells: plateMap.wells.length,
                    },
                  };
                  // TODO(BNCH-109904): This won't trigger for step config documents, so re-address when plate
                  // designs are allowed there.
                  waitForEntryOrTemplateSaved(props.fileId, 60, 500).then((saved: boolean) => {
                    assertAndContinue(saved, 'Failed to save plate design in 60s');
                    if (saved) {
                      logProfilerTimingEvent(
                        {...CREATE_PLATE_DESIGN_FINISHED_EVENT, id: profilerId},
                        profilerMetadata
                      );
                      const analyticsMetadata = {
                        actionOrigin: isInProcedure ? ActionOrigin.WORKSHEET : ActionOrigin.NOTEBOOK,
                        eventGroupId: props.eventGroupId,
                        fileId: props.fileId,
                      };
                      analytics.trackEventWithModule(
                        ELN_ANALYTICS_ACTIONS.CREATED_PLATE_DESIGN,
                        ANALYTICS_MODULE.ELN,
                        analyticsMetadata
                      );
                    }
                  });
                } else {
                  // If we aren't creating a new plate design, we will have data on the existing plate design
                  // even though ts doesn't know that.
                  const plateDesignVersionRef = props.initiatingPlateDesignState
                    ?.plateDesignVersionRef as PlateDesignVersionRef;
                  analytics.trackEventWithModule(
                    ELN_ANALYTICS_ACTIONS.UPDATED_PLATE_DESIGN,
                    ANALYTICS_MODULE.ELN,
                    {
                      actionOrigin: isInProcedure ? ActionOrigin.WORKSHEET : ActionOrigin.NOTEBOOK,
                      eventGroupId: props.eventGroupId,
                      fileId: props.fileId,
                      plateDesignId: plateDesignVersionRef.plateDesignApiId,
                      plateDesignVersionId: plateDesignVersionRef.plateDesignVersionApiId,
                    }
                  );
                }
              });
            }}
            procedureContext={{
              plateDataParameters,
              plateMaterialInputs,
              isInProcedure,
              procedureStudyDesignType,
              procedureId,
              procedureTestOrderConfig,
              procedureMethodExecutionInstanceId,
            }}
            eventGroupId={props.eventGroupId}
            note={props.note}
            fileId={props.fileId}
            studyableProps={getStudyablePropsFromWorksheet(worksheet)}
            initiatingPlateDesignState={props.initiatingPlateDesignState}
            plateDesignNames={existingPlateDesignNames}
          />
        </ModalAnchor>
      )}
    </ParametersForPlateMapWrapper>
  );
}
